/**
 * Geometry-matched loading skeletons (DESIGN_UPGRADE §3.8). Each skeleton mirrors the real
 * component's shape — a gauge is a ring outline, a KPI is a tile with a bar where the number goes,
 * a chart is an axis + faint area — never a generic grey rectangle grid. The shimmer band is the
 * shared `--sheen` (see `.skel` in components.css); prefers-reduced-motion drops it to a static tint.
 */

function GaugeSkeleton(): JSX.Element {
  return (
    <div className="card skgauge">
      <div className="skel ring" />
      <div className="skel b" style={{ height: 14 }} />
      <div className="skel" style={{ height: 22, width: 92, borderRadius: 999 }} />
      <div className="skel" style={{ height: 40, width: '100%', marginTop: 8 }} />
    </div>
  );
}

/** Dashboard: hero chart + live rail, then last-message results + number status glance. */
export function DashboardSkeleton(): JSX.Element {
  return (
    <div className="content">
      <div className="skel sk-line" style={{ marginBottom: 28 }} />
      <div className="hero">
        <div className="card skchart"><div className="skel area" /></div>
        <div className="card" style={{ padding: 22 }}>
          <div className="skel" style={{ height: 14, width: '40%', marginBottom: 16 }} />
          <div className="skel" style={{ height: 20, width: '80%', marginBottom: 10 }} />
          <div className="skel" style={{ height: 10, width: '100%', marginBottom: 18, borderRadius: 999 }} />
          <div className="skel" style={{ height: 60, width: '100%' }} />
        </div>
      </div>
      <div className="lower">
        <div className="card" style={{ padding: 22 }}>
          <div className="skel" style={{ height: 18, width: '45%', marginBottom: 18 }} />
          <div className="skel" style={{ height: 56, width: '100%', marginBottom: 8, borderRadius: 12 }} />
          <div className="skel" style={{ height: 56, width: '82%', marginBottom: 8, borderRadius: 12 }} />
          <div className="skel" style={{ height: 56, width: '64%', borderRadius: 12 }} />
        </div>
        <div className="card" style={{ padding: 22 }}>
          <div className="skel" style={{ height: 18, width: '50%', marginBottom: 18 }} />
          <div className="skel" style={{ height: 40, width: '60%', marginBottom: 12 }} />
          <div className="skel" style={{ height: 22, width: 120, borderRadius: 999 }} />
        </div>
      </div>
    </div>
  );
}

/** Sessions: a grid of number cards (gauge + meters). */
export function SessionsSkeleton(): JSX.Element {
  return (
    <div className="sessions-grid">
      {[0, 1, 2, 3].map((i) => <GaugeSkeleton key={i} />)}
    </div>
  );
}

/** Generic table skeleton (Campaigns / Targets / Activity) — header bar + rows. */
export function TableSkeleton({ rows = 5 }: { rows?: number }): JSX.Element {
  return (
    <div style={{ padding: 16 }}>
      <div className="skel" style={{ height: 20, width: '30%', marginBottom: 14 }} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height: 44, marginBottom: 8 }} />
      ))}
    </div>
  );
}
